import '@avalara/bat-apps-shared/client/styles.css';
import debounce from 'lodash/debounce';
import { useEffect } from 'react';
import Loadable from 'react-loadable';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import {
  initializeDigitalData,
  updateAnalyticsPage,
  updateAnalyticsUser,
} from '@avalara/bat-apps-shared/helpers/analytics';
import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import { AuiSessionTimeout } from '@avalara/skylab-sdk/react';

import { getCurrentDevice } from '../components/helpers';
import Loading from '../components/loading';
import PollingDialog from '../components/shared/polling-dialog';
import SimpleMessage from '../components/simple-message/simple-message';
import { AppWrapper } from '../components/styled/components/app';
import { AppRoot } from '../components/styled/skylab/page';
import SystemMessage from '../components/system-message';
import conf from '../conf';
import { hideHeaderUrlInQa, setHeaderLoginAndLogoutUrl, setHeaderRequestCurrentAccessToken } from '../lib/aui-header';
import { checkIsAuthenticated, getReturnsPagename } from '../lib/helpers';
import { authRedirect, initABTesting } from '../lib/side-effects';
import { fetchAccountForAvaTax, fetchAccountForReturns, fetchAccountForService } from '../lib/side-effects/accounts';
import { getSystemErrorMessaging } from '../lib/side-effects/error';
import { configureFeatures } from '../lib/side-effects/features';
import { selectState } from '../selectors';
import { isAuthenticationRequiredSelector } from '../selectors/offer';
import { setAbGroup } from '../store/ab-testing';
import { setAccountFetchFailed, setIsMobile } from '../store/system';
import ErrorBoundary from './error-boundary';
import { redirectFromKnownReferrer } from './helpers/referrerBehavior';

import type { Dispatch } from '@avalara/types/actions';
import { isOneWebDomainRuntime } from '../helpers';

const BuyFlow = Loadable({ loader: () => import('./buy-flow'), loading: Loading });
const Esb = Loadable({ loader: () => import('./esb'), loading: Loading });
const FreeTrial = Loadable({ loader: () => import('./free-trial'), loading: Loading });
const Home = Loadable({ loader: () => import('../components/home-page'), loading: Loading });
const Offer = Loadable({ loader: () => import('./offer'), loading: Loading });
const Returns = Loadable({ loader: () => import('./returns'), loading: Loading });
const RiskAssessment = Loadable({ loader: () => import('./risk-assessment'), loading: Loading });
const Question = Loadable({ loader: () => import('./prequal-questionnaire'), loading: Loading });
const AccountsPayable = Loadable({ loader: () => import('../components/accounts-payable'), loading: Loading });

const App = () => {
  const dispatch = useDispatch<Dispatch>();
  const location = useLocation();

  const {
    abTesting: { group },
    account: { accountId, isLoggedIn },
    data: { isLoading, showingLoadingRocket },
    offer: { allowedUsers, offerFlow, offerName },
    system: { accountFetchFailed, activeFlow, error, isPollingDialogOpen },
  } = useSelector(selectState);

  const isAuthenticationRequired = useSelector(isAuthenticationRequiredSelector);

  const onDeviceDetect = () => {
    dispatch(setIsMobile(getCurrentDevice()));
  };

  const setPageAnalytics = () => {
    // Note (Andrew) - getReturnsPagename returns an empty string for non-returns flows.
    //                 updateAnalyticsPage will automatically set the desired analytics that
    //                 we want to capture when '' is passed, but we need to explicitly set
    //                 the page name for the returns flows, since the same /returns path is
    //                 used for selling both MRS as well as RSB.
    const pageNameOverride = getReturnsPagename();
    updateAnalyticsPage('buydot', (s) => s, pageNameOverride);
  };

  const initialAnalyticsLoad = () => {
    /* Note(Sasha): Need to clear digitalData in case page view was called before
       the Adobe Analytics script finished loading. */
    initializeDigitalData('Buydot | Avalara');
    setPageAnalytics();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (Boolean(document.referrer) && !document.referrer.includes(new URL(conf.CLIENT_URL).hostname))
      sessionStorage.set('referrer', document.referrer);
    redirectFromKnownReferrer(dispatch, document, conf);

    dispatch(configureFeatures(location.search));

    let envTitle = '';
    if (window.appEnv === 'prod') envTitle = 'Staging';
    if (window.appEnv === 'qa') envTitle = 'QA';
    if (window.appEnv === 'local') envTitle = 'Local';
    // this will serve as a good final check to make sure eb env has `islive` set as well
    if (
      !isOneWebDomainRuntime() &&
      (window.appEnv !== 'prod' || /staging.buy.avalara.com/.test(window.location.href))
    ) {
      const prompt = document.createElement('P');
      const text = document.createTextNode(`You are working in the ${envTitle} environment`);
      prompt.appendChild(text);
      prompt.className += ` ${window.appEnv || ''}-header`;
      if (document.body) document.body.insertBefore(prompt, document.body.firstChild);
      if (document.body) document.body.className += ` ${window.appEnv || ''}-env`;
    }

    onDeviceDetect();
    // Add event listener to toggle mobile view
    window.addEventListener('resize', () => debounce(onDeviceDetect, 100));

    initABTesting((abGroup) => dispatch(setAbGroup(abGroup)), group);

    if (window.appEnv !== 'prod') hideHeaderUrlInQa();

    /* NOTE(Sasha): Need to wait for app/scripts to load first before
       setting page analytics to avoid the race condition where we fire
       analytics calls but the Adobe script has not loaded */
    window.addEventListener('load', initialAnalyticsLoad);

    return () => {
      // Remove event listener
      window.removeEventListener('resize', onDeviceDetect);
      window.removeEventListener('load', initialAnalyticsLoad);
    };
  }, []);

  useEffect(() => {
    updateAnalyticsUser({ isLoggedIn: isLoggedIn ? 'YES' : 'NO', accountId: (accountId || '').toString() });
  }, [isLoggedIn, accountId]);

  // Clear accountFetchFailed only when truly leaving offer section (path is /buy or /buy/),
  // not when URL was corrupted to /buy/https:... (which clears offerFlow and would otherwise clear the flag).
  const isOnOfferPath =
    location.pathname !== '/buy' && location.pathname !== '/buy/' && /^\/buy\/[^/]+/.test(location.pathname);
  useEffect(() => {
    if (!offerFlow && accountFetchFailed && !isOnOfferPath) {
      dispatch(setAccountFetchFailed(false));
    }
  }, [offerFlow, accountFetchFailed, isOnOfferPath]);

  // window.aui.user.isLoggedIn indicates that a user has an active identity session.
  // isLoggedIn is set after fetching a user's account (this variable _should_ be named hasAccountBeenFetched).
  // if user has an active identity session but isLoggedIn == false,
  // we haven't fetched their account yet, so do it now.
  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    const isAuthenticated = checkIsAuthenticated();

    if (isLoggedIn) return;

    if (allowedUsers === 'NEW' && isAuthenticated) {
      if (offerFlow === 'free-trial' || offerFlow === 'avatax-free-trial') {
        dispatch(getSystemErrorMessaging({ errorCode: 'freeTrialNotAvailableForExistingAccounts', offerName }));
        return;
      }
      dispatch(getSystemErrorMessaging({ errorCode: 'notAvailableForExistingAccounts', offerName }));
      return;
    }

    if (offerFlow) {
      if (accountFetchFailed) return;
      if (isAuthenticated) {
        dispatch(fetchAccountForService());
      } else if (isAuthenticationRequired) {
        authRedirect();
      }
      return;
    }

    if (!isAuthenticated || !activeFlow) return;

    if (/returns/.test(activeFlow)) {
      dispatch(fetchAccountForReturns('returns'));
      return;
    }

    // fetchAccountForAvaTax should not be called for esb-trial. It should be called for any other activeFlows containing "avatax" or "trial"
    // unless the activeFlow contains "returns". In that case, it is handled by fetchAccountForReturns which is called above.
    if (!['esb-trial'].includes(activeFlow) && (/avatax/.test(activeFlow) || /trial/.test(activeFlow))) {
      dispatch(fetchAccountForAvaTax());
      return;
    }

    dispatch(fetchAccountForService());
  }, [isLoggedIn, activeFlow, offerFlow, allowedUsers, offerName, accountFetchFailed]);

  // Note(Andrew) - Update the loginUrl in the aui header any time the location changes,
  // so that the user will get properly redirected to the same page they're already on
  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(setHeaderLoginAndLogoutUrl, [location]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const isEmbedded = queryParams.get('embedded') !== null;
    if (isOneWebDomainRuntime() && isEmbedded) {
      document.querySelector('aui-header')?.remove();
      document.querySelector('aui-footer')?.remove();
    }
  }, [location.search]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(setPageAnalytics, [location.pathname, location.search]);

  useEffect(() => {
    if (isLoggedIn) {
      setHeaderRequestCurrentAccessToken();
    }
  }, [isLoggedIn]);

  const MessageComponent = error?.displaySimpleMessage ? SimpleMessage : SystemMessage;

  const showLoader = isLoading && !showingLoadingRocket;

  const renderError = () => {
    // Should never happen just make ts happy
    if (!error) return null;

    return <MessageComponent messaging={error} />;
  };

  return (
    <ErrorBoundary>
      <AppWrapper>
        {error && renderError()}
        {!error && showLoader && <Loading />}
        {!error && isPollingDialogOpen && <PollingDialog />}
        {checkIsAuthenticated() && !isOneWebDomainRuntime() && (
          <AuiSessionTimeout onS-timeout={() => window.location.assign('/auth/logout')} />
        )}
        <AppRoot isFetchingData={showLoader}>
          <div>
            <Switch>
              <Route exact path='/' component={Home} />
              <Route path='/accounts-payable' component={AccountsPayable} />
              <Route path='/buy' component={BuyFlow} />
              {/* The old signup flow allowed users to buy AvaTax instead of signing up for a trial by
                visiting "/signup?buynow=true". Redirect to handle anyone still sending users there. */}
              {/buynow=true/i.test(location.search) && (
                <Route path='/signup'>
                  <Redirect to={{ pathname: '/buy', search: location.search }} />
                </Route>
              )}
              <Route path='/state-license'>
                <Redirect to={{ pathname: '/avalara-licensing', search: location.search }} />
              </Route>
              <Route path='/free-trial/buy' component={FreeTrial} />
              <Route path='/returns' component={Returns} />
              {!/showNewRisk=true/i.test(location.search) && (
                <Route path='/risk-assessment' component={RiskAssessment} />
              )}
              <Route path={['/start', '/start-trial']} component={Esb} />
              <Route path='/question' component={Question} />
              <Route path='/:requestedOfferFlow' component={Offer} />
            </Switch>
          </div>
        </AppRoot>
      </AppWrapper>
    </ErrorBoundary>
  );
};

export default App;
